import type { CmtsHttpClient } from "../api/cmtsHttpClient.js";
import { CMTS_API } from "../api/cmtsApiPaths.js";
import { mapCapa, mapComplaintToCapaQueue, type CmtsCapaVm } from "../mappers/rcaCapaMappers.js";
import { validateCapa } from "../validators/capaValidators.js";
import { repositoryCall, type RepositoryResult } from "./repositoryResult.js";
import { readString } from "../mappers/mapperUtils.js";
import { mapCollection, extractItems } from "./repositoryCollection.js";   // extractItems add
import { requireCmtsGuid } from "../utils/cmtsGuid.js";

export type CapaRepository = {
  queue(tenantId?: string, query?: Record<string, unknown>): Promise<RepositoryResult<readonly CmtsCapaVm[]>>;
  list(complaintId: string, tenantId?: string): Promise<RepositoryResult<readonly CmtsCapaVm[]>>;
  create(complaintId: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<CmtsCapaVm>>;
  complete(complaintId: string, capaId: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<CmtsCapaVm>>;
};

function queueQuery(tenantId?: string, query: Record<string, unknown> = {}): Record<string, string | number | boolean | undefined> {
  const rawSearch = typeof query.search === "string" ? query.search : typeof query.searchText === "string" ? query.searchText : undefined;
  return {
    tenantId,
    search: rawSearch,
    page: typeof query.page === "number" ? query.page : typeof query.pageNumber === "number" ? query.pageNumber : 1,
    pageSize: typeof query.pageSize === "number" ? query.pageSize : 100,
  };
}

export function createCapaRepository(client: CmtsHttpClient): CapaRepository {
  return {
queue: (tenantId, query = {}) => repositoryCall(async () => {
  const complaintDtos = extractItems(await client.get<unknown>(CMTS_API.complaints.list, { query: queueQuery(tenantId, query) }));
  const rowsPerComplaint = await Promise.all(
    complaintDtos.map(async (complaintDto): Promise<readonly CmtsCapaVm[]> => {
      const complaintId = readString(complaintDto, ["id", "complaintId"], "");
      if (!complaintId) return [mapComplaintToCapaQueue(complaintDto)];
      try {
        const capaDtos = extractItems(await client.get<unknown>(CMTS_API.complaints.capa(complaintId), { query: { tenantId } }));
        if (capaDtos.length) return capaDtos.map((capaDto) => mapCapa(capaDto));
        return [mapComplaintToCapaQueue(complaintDto)];
      } catch {
        return [mapComplaintToCapaQueue(complaintDto)];
      }
    }),
  );
  return rowsPerComplaint.flat();
}, "CMTS_CAPA_QUEUE"),    list: (id, tenantId) => repositoryCall(async () => {
      const complaintId = requireCmtsGuid(id, "CAPA list complaintId");
      return mapCollection(await client.get<unknown>(CMTS_API.complaints.capa(complaintId), { query: { tenantId } }), mapCapa);
    }, "CMTS_CAPA_LIST"),
    create: (id, payload, tenantId) => repositoryCall(async () => {
      const complaintId = requireCmtsGuid(id, "CAPA create complaintId");
      const d = validateCapa(payload);
      if (d.length) throw { success: false, message: "CAPA validation failed", errors: d.map((x) => ({ code: x.code, field: x.field, message: x.message })) };
      return mapCapa(await client.post<unknown>(CMTS_API.complaints.capa(complaintId), { ...payload, tenantId, complaintId }, { query: { tenantId } }));
    }, "CMTS_CAPA_CREATE"),
    complete: (id, capaId, payload, tenantId) => repositoryCall(async () => {
      const complaintId = requireCmtsGuid(id, "CAPA complete complaintId");
      const backendCapaId = requireCmtsGuid(capaId, "CAPA complete capaId");
      return mapCapa(await client.post<unknown>(CMTS_API.complaints.completeCapa(complaintId, backendCapaId), payload, { query: { tenantId } }));
    }, "CMTS_CAPA_COMPLETE")
  };
}
